import React, { useContext, useState, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux'
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles'
import Button from '@mui/material/Button';
import OutlinedInput, { OutlinedInputProps, outlinedInputClasses } from '@mui/material/OutlinedInput';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import { OpenAPIV3_1 as OpenAPI } from "openapi-types";
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import Parameters from './Parameters';
import { PathContext } from './Methods';
import { DocContext } from '../Documentation';
import { ApiResponse } from 'src/api';
import { resolve, getParams, getRequestBody, getRequestBodyModel, isApiResponseObject } from '../utils';
import formatHighlight from 'json-format-highlight';
import { Typography } from '@mui/material';
import { addSnackbar } from 'src/domains/global/actions';
import _, { Dictionary } from 'lodash';

const InputRoot = styled(OutlinedInput)<{
    ownerState: OutlinedInputProps & { error: boolean };
}>(({ theme, ownerState }) => (ownerState.error && {
    [`&.${outlinedInputClasses.root} .${outlinedInputClasses.notchedOutline}`]: {
        borderColor: theme.palette.error.main
    }
}));

const RequestBox: React.FC<{
    operationObject: OpenAPI.OperationObject,
    runRequest: (
        pathKey: string,
        pathParams: Dictionary<string>,
        queryParams: Dictionary<string>,
        body: any
    ) => Promise<ApiResponse>

}> = ({
    operationObject,
    runRequest
}) => {
        const dispatch = useDispatch();
        const document = useContext(DocContext)!;
        const pathCtx = useContext(PathContext)!;
        const { pathKey, pathItemObject } = pathCtx;

        const [tabNum, setTabNum] = React.useState(0);
        const [responseTabNum, setResponseTabNum] = React.useState(0);

        const [parameters, setParameters] = useState<OpenAPI.ParameterObject[]>([]);
        const [paramVals, setParamVals] = useState({});
        const [response, setResponse] = useState<ApiResponse | null>(null);
        const [resHeaders, setResHeaders] = useState({});

        const requestBodyObj: OpenAPI.RequestBodyObject | undefined = useMemo<OpenAPI.RequestBodyObject | undefined>(() => {
            return operationObject.requestBody ? resolve(operationObject.requestBody, document)! : undefined;
        }, [operationObject, document]);

        const mediaTypes: string[] = useMemo<string[]>(() => {
            return requestBodyObj ? _.keys(requestBodyObj.content) : [];
        }, [requestBodyObj]);

        const [mediaType, setMediaType] = useState<string | undefined>(mediaTypes.length > 0 ? mediaTypes[0] : undefined);
        const [media, setMedia] = useState<OpenAPI.MediaTypeObject | undefined>(mediaType ? requestBodyObj!.content[mediaType] : undefined);

        const [body, setBody] = useState<string>('');
        const [bodyModel, setBodyModel] = useState<any>({});
        const [syntaxError, setSyntaxError] = useState(false);

        const handleChange = (event: SelectChangeEvent) => {
            const mediaType: string = event.target.value;
            setMediaType(mediaType);
            setMedia(requestBodyObj!.content[mediaType]);
        };

        const handleEditBody = (event: any) => {
            setBody(event.target.value);
        };

        const handleTabNum = (event: React.SyntheticEvent, newValue: number) => {
            setTabNum(newValue);
        };

        const handleResponseTabNum = (event: React.SyntheticEvent, newValue: number) => {
            setResponseTabNum(newValue);
        };

        const schema: OpenAPI.SchemaObject | undefined = useMemo<OpenAPI.SchemaObject | undefined>(() => {
            return media ? resolve(media?.schema, document)! : undefined;
        }, [media, document]);

        useEffect(
            () => {
                setParameters(getParams(pathItemObject, operationObject, document));
            },
            [pathItemObject, operationObject, document]
        );

        useEffect(
            () => {
                setBody(media ? getRequestBody(media, document) : '');
            },
            [media, document]
        );

        useEffect(
            () => {
                setBodyModel(media ? getRequestBodyModel(resolve(media.schema, document)!, document) : {});
            },
            [media, document]
        );

        useEffect(
            () => {
                const headers: any = {};
                if (response) {
                    for (var key of response.headers.keys()) {
                        headers[key] = response.headers.get(key);
                    }
                }
                setResHeaders(headers);
            },
            [response]
        );

        const handleRequest = () => {
            const pathParams = _.filter(parameters, param => param.in === 'path');
            const queryParams = _.filter(parameters, param => param.in === 'query');

            try {
                const requestBody = (body !== '') ?
                    schema?.type === 'string' ? body : JSON.parse(body)
                    :
                    null;

                runRequest(
                    pathKey,
                    _.pick(paramVals, pathParams.map(p => p.name)),
                    _.pick(paramVals, queryParams.map(p => p.name)),
                    requestBody,
                )
                    .then(response => {
                        setResponse(response);
                        setSyntaxError(false);
                    })
                    .catch(err => {
                        if (isApiResponseObject(err)) {
                            setResponse(err);
                        } else {
                            dispatch(addSnackbar('Error', 'error', {}));
                        }
                    })

            } catch (err) {
                if (err instanceof SyntaxError) {
                    setSyntaxError(true);
                }
            }
        }

        const clear = () => {
            setResponse(null);
            setSyntaxError(false);
        }

        return <Box>
            <Parameters
                operationObject={operationObject}
                values={paramVals}
                onChange={(key, value) => {
                    setParamVals({ ...paramVals, [key]: value })
                }}
            />
            {
                requestBodyObj && <>
                    <Box sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        alignItems: 'center'
                    }}>
                        <Tabs
                            sx={{ overflow: 'inherit' }}
                            value={tabNum}
                            onChange={handleTabNum}
                        >
                            <Tab label="Value" />
                            <Tab label="Model" />
                        </Tabs>
                        {
                            mediaTypes.length > 0 && <Box sx={{ flex: 1, textAlign: 'right' }}>
                                <FormControl size='small'>
                                    <Select
                                        value={mediaType}
                                        onChange={handleChange}
                                    >
                                        {
                                            mediaTypes.map(mediaType => (
                                                <MenuItem
                                                    value={mediaType}
                                                    key={mediaType}>
                                                    {mediaType}
                                                </MenuItem>
                                            ))
                                        }

                                    </Select>
                                </FormControl>
                            </Box>
                        }
                    </Box>

                    {
                        tabNum === 0 && body !== null && <Box sx={{
                            fontSize: '0.8em',
                            mt: 1,
                            mb: 1
                        }}>
                            <FormControl
                                size='small'
                                fullWidth
                            >

                                <InputRoot
                                    ownerState={{
                                        error: syntaxError,
                                    }}
                                    value={body}
                                    onChange={handleEditBody}
                                    multiline
                                />

                            </FormControl>
                        </Box>
                    }
                    {
                        tabNum === 1 && <Box sx={{ fontSize: '0.8em' }}>
                            <pre dangerouslySetInnerHTML={{ __html: formatHighlight(bodyModel) }} />
                        </Box>
                    }
                </>
            }
            <Box sx={{
                display: 'flex',
                alignItems: 'center'
            }}>
                <Button variant='contained' onClick={handleRequest}>{'Try'}</Button>
                {
                    response !== null && <Typography sx={{
                        fontWeight: 700,
                        pl: 1,
                        pr: 1
                    }}>{response.status}</Typography>
                }
                <Box sx={{
                    flex: 1,
                    textAlign: 'right'
                }}>
                    <Button variant='outlined' onClick={clear}>{'Clear'}</Button>
                </Box>
            </Box>
            {
                response !== null && <Box>
                    <Tabs
                        sx={{ overflow: 'inherit' }}
                        value={responseTabNum}
                        onChange={handleResponseTabNum}
                    >
                        <Tab label="Response" />
                        <Tab label="Headers" />
                    </Tabs>
                    <Box sx={{ fontSize: '0.8em', maxHeight: 500, overflow: 'auto' }}>
                        {
                            responseTabNum === 0 && <pre dangerouslySetInnerHTML={{ __html: formatHighlight(response.json) }} />
                        }
                        {
                            responseTabNum === 1 && <pre dangerouslySetInnerHTML={{ __html: formatHighlight(resHeaders) }} />
                        }
                    </Box>
                </Box>
            }
        </Box>;
    }

export default RequestBox;
